/**
 * Security Helper - Handles input validation, sanitization, and security measures
 */

// Input validation patterns
const VALIDATION_PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^\+?[\d\s\-\(\)]{10,15}$/,
  PROMO_CODE: /^[a-zA-Z0-9_-]{1,50}$/,
  NAME: /^[a-zA-Z\s]{2,50}$/,
  NUMERIC_ID: /^\d+$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
};

// SQL injection patterns to detect and block
const SQL_INJECTION_PATTERNS = [
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE)\b)/i,
  /(\b(UNION|OR|AND)\s+\d+\s*=\s*\d+)/i,
  /(--|\/\*|\*\/|;)/,
  /(\b(SCRIPT|JAVASCRIPT|VBSCRIPT|ONLOAD|ONERROR)\b)/i,
  /(<script[\s\S]*?>[\s\S]*?<\/script>)/i,
];

// XSS patterns to detect and block
const XSS_PATTERNS = [
  /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
  /<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi,
  /<object[\s\S]*?>[\s\S]*?<\/object>/gi,
  /<embed[\s\S]*?>[\s\S]*?<\/embed>/gi,
  /<form[\s\S]*?>[\s\S]*?<\/form>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
];

/**
 * Sanitize string input by removing dangerous characters
 * @param {string} input - Input string to sanitize
 * @param {string} type - Type of sanitization ('text', 'email', 'numeric', etc.)
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input, type = "text") => {
  if (!input || typeof input !== "string") return "";

  let sanitized = input.trim();

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, "");

  // Check for SQL injection attempts
  for (const pattern of SQL_INJECTION_PATTERNS) {
    if (pattern.test(sanitized)) {
      console.warn("Potential SQL injection attempt detected");
      return "";
    }
  }

  // Check for XSS attempts
  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(sanitized)) {
      console.warn("Potential XSS attempt detected");
      return "";
    }
  }

  switch (type) {
    case "email":
      // Keep only valid email characters
      sanitized = sanitized.replace(/[^a-zA-Z0-9._%+-@]/g, "");
      break;
    case "numeric":
      // Keep only numbers
      sanitized = sanitized.replace(/[^0-9]/g, "");
      break;
    case "alphanumeric":
      // Keep only letters and numbers
      sanitized = sanitized.replace(/[^a-zA-Z0-9]/g, "");
      break;
    case "name":
      // Keep only letters and spaces
      sanitized = sanitized.replace(/[^a-zA-Z\s]/g, "");
      break;
    case "promo_code":
      // Keep only valid promo code characters
      sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, "");
      break;
    case "text":
    default:
      // Remove potentially dangerous characters but keep basic text
      sanitized = sanitized.replace(/[<>\"'&]/g, "");
      break;
  }

  return sanitized;
};

/**
 * Validate input against specific pattern
 * @param {string} input - Input to validate
 * @param {string} type - Type of validation
 * @returns {boolean} - Whether input is valid
 */
export const validateInput = (input, type) => {
  if (input === undefined || input === null) return false;

  // Special handling for numeric type - accept both numbers and numeric strings
  if (type.toUpperCase() === "NUMERIC") {
    if (typeof input === "number") {
      return Number.isInteger(input) && input >= 0;
    }
    if (typeof input === "string") {
      const trimmed = input.trim();
      return /^\d+$/.test(trimmed);
    }
    return false;
  }

  if (typeof input !== "string") return false;

  const pattern = VALIDATION_PATTERNS[type.toUpperCase()];
  if (!pattern) return false;

  return pattern.test(input.trim());
};

/**
 * Rate limiting for API calls and user actions
 */
const rateLimitMap = new Map();

export const checkRateLimit = (key, windowMs = 60000, maxAttempts = 5) => {
  const now = Date.now();
  const windowStart = now - windowMs;

  if (!rateLimitMap.has(key)) {
    rateLimitMap.set(key, []);
  }

  const attempts = rateLimitMap.get(key);

  // Remove old attempts outside the window
  const recentAttempts = attempts.filter((time) => time > windowStart);

  if (recentAttempts.length >= maxAttempts) {
    return false; // Rate limit exceeded
  }

  // Add current attempt
  recentAttempts.push(now);
  rateLimitMap.set(key, recentAttempts);

  return true; // Within rate limit
};

/**
 * Secure data encryption for localStorage
 * @param {any} data - Data to encrypt
 * @returns {string} - Encrypted data
 */
export const encryptData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    return btoa(encodeURIComponent(jsonString));
  } catch (error) {
    console.error("Encryption failed");
    return null;
  }
};

/**
 * Secure data decryption for localStorage
 * @param {string} encryptedData - Encrypted data string
 * @returns {any} - Decrypted data
 */
export const decryptData = (encryptedData) => {
  try {
    const jsonString = decodeURIComponent(atob(encryptedData));
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Decryption failed");
    return null;
  }
};

/**
 * Check for suspicious activity patterns
 * @param {string} userAgent - User agent string
 * @param {array} recentActions - Array of recent user actions
 * @returns {boolean} - Whether activity seems suspicious
 */
export const detectSuspiciousActivity = (userAgent, recentActions = []) => {
  // Check for bot patterns in user agent
  const botPatterns = [
    /bot/i,
    /crawler/i,
    /spider/i,
    /scraper/i,
    /headless/i,
    /phantom/i,
    /puppeteer/i,
    /selenium/i,
  ];

  for (const pattern of botPatterns) {
    if (pattern.test(userAgent)) {
      return true;
    }
  }

  // Check for rapid successive actions (potential bot behavior)
  if (recentActions.length > 10) {
    const timeSpan = recentActions[recentActions.length - 1] - recentActions[0];
    if (timeSpan < 5000) {
      // 10 actions in less than 5 seconds
      return true;
    }
  }

  return false;
};

/**
 * Generate secure random token
 * @param {number} length - Token length
 * @returns {string} - Random token
 */
export const generateSecureToken = (length = 32) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return Date.now().toString(36) + result;
};

/**
 * Validate and sanitize booking payload
 * @param {object} payload - Booking payload to validate
 * @returns {object} - Validated and sanitized payload or null if invalid
 */
export const validateBookingPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    console.warn("Invalid payload: not an object");
    return null;
  }

  // Required fields validation
  const requiredFields = [
    "courtId",
    "startDateTime",
    "endDateTime",
    "paymentTypeId",
  ];
  for (const field of requiredFields) {
    if (!payload[field] && payload[field] !== 0) {
      console.warn(`Missing required field: ${field}`);
      return null;
    }
  }

  // Sanitize string fields
  const sanitizedPayload = { ...payload };
  if (sanitizedPayload.promocode) {
    sanitizedPayload.promocode = sanitizeInput(
      sanitizedPayload.promocode,
      "promo_code"
    );
  }

  // Validate numeric fields - these can be numbers or numeric strings
  const numericFields = [
    "courtId",
    "paymentTypeId",
    "totalAmount",
    "finalAmount",
  ];
  for (const field of numericFields) {
    if (
      sanitizedPayload[field] !== undefined &&
      sanitizedPayload[field] !== null
    ) {
      if (!validateInput(sanitizedPayload[field], "numeric")) {
        console.warn(
          `Invalid numeric field: ${field}`,
          sanitizedPayload[field],
          typeof sanitizedPayload[field]
        );
        return null;
      }
    }
  }

  // Validate date fields
  const dateFields = ["startDateTime", "endDateTime"];
  for (const field of dateFields) {
    if (sanitizedPayload[field]) {
      const date = new Date(sanitizedPayload[field]);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date field: ${field}`);
        return null;
      }
    }
  }

  return sanitizedPayload;
};

/**
 * Security headers for API requests
 */
export const getSecurityHeaders = () => {
  return {
    "X-Content-Type-Options": "nosniff",
    "X-Frame-Options": "DENY",
    "X-XSS-Protection": "1; mode=block",
    "Referrer-Policy": "strict-origin-when-cross-origin",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  };
};

/**
 * Log security events (in production, this would send to a security monitoring service)
 * @param {string} event - Security event type
 * @param {object} details - Event details
 */
export const logSecurityEvent = (event, details = {}) => {
  // In development, just log to console
  // In production, this should send to a security monitoring service
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...details,
  });
};

export default {
  sanitizeInput,
  validateInput,
  checkRateLimit,
  encryptData,
  decryptData,
  detectSuspiciousActivity,
  generateSecureToken,
  validateBookingPayload,
  getSecurityHeaders,
  logSecurityEvent,
};
